"use client";

/**
 * SmartButton
 * Intelligent button/link for the public view that detects link types:
 * - Form links (/{eventId}/form/{formId}) → opens FormModal
 * - Hotel-booking links (/{eventId}/hotel-booking/{bookingLinkId}) → opens HotelBookingModal
 * - External links (https://...) → opens in new tab
 * - Anchor links (#section) → smooth scroll
 * - Email links (#__email-{templateId}) → EmailSendButton when a button id is given, otherwise an inert disabled button
 * - Other links → regular navigation
 * A form or hotel-booking button with `linkedSteps` runs every step in order instead of one modal.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { isAnchorLink, isExternalLink } from "../../_logic/smart-link-utils";
import { getStickyHeaderOffset } from "../../_logic/utils/scroll-utils";
import { prefetchForm } from "../../_logic/form-prefetch";
import { prefetchHotelBookingModal } from "../../_logic/hotel-booking-modal-prefetch";
import type { EmailButtonSendOutcome } from "../../_hooks/useEmailButtonSend";
import { useHotelBookingCta } from "../../_hooks/useHotelBookingCta";
import { useLinkedStepsCta } from "../../_hooks/useLinkedStepsCta";
import { useSmartLinkTarget } from "../../_hooks/useSmartLinkTarget";
import { useSyntheticClickGuard } from "../../_hooks/useSyntheticClickGuard";
import type { OrderContext } from "./CheckoutStep";
import { EmailSendButton } from "./EmailSendButton";
import { FormModal } from "./FormModal";
import { usePublicViewContext } from "./PublicViewContext";

interface SmartButtonProps {
  /** The link URL — form, external, anchor, or relative */
  href: string;
  /** Button label text */
  label: string;
  /** Primary color for FormModal styling */
  primaryColor: string;
  /** Event ID for form link detection */
  eventId: number;
  /** Optional Tailwind classes (overrides default styling) */
  className?: string;
  /** Optional inline styles (merged with internal backgroundColor) */
  style?: React.CSSProperties;
  /** Optional context about the item being purchased (shown in checkout summary) */
  orderContext?: OrderContext;
  /**
   * Id of the CTA button this renders. Only hero, textButton, and navbar buttons pass it, and an
   * email link sends only when it is set. Without it an email link stays inert.
   */
  buttonId?: string;
  /**
   * Steps to run after `href`, in order (see `CtaButton.linkedSteps`). Ignored unless `href` is a form
   * or hotel-booking link; without it the button opens a single modal as before.
   */
  linkedSteps?: string[];
}

/**
 * Analytics event fired on every CTA activation so a dead primary CTA surfaces in
 * analytics instead of only in a session recording. The `outcome` distinguishes a
 * healthy activation from one that resolved to nothing (missing anchor, blocked scheme).
 */
const CTA_ACTIVATED_EVENT = "landing_page_cta_activated";

/** Every `outcome` value recorded on {@link CTA_ACTIVATED_EVENT}. */
type CtaActivationOutcome =
  | "form"
  | "hotel_booking"
  | "anchor"
  | "anchor_unresolved"
  | "external"
  | "internal"
  | "blocked_scheme"
  | EmailButtonSendOutcome;

/** Renders a button or link that adapts its behavior based on the link type */
export function SmartButton({
  href,
  label,
  primaryColor,
  eventId,
  className,
  style,
  orderContext,
  buttonId,
  linkedSteps,
}: SmartButtonProps) {
  // Hooks
  const { landingPageId, eventId: pageEventId } = usePublicViewContext();
  const posthog = usePostHog();
  const { shouldIgnoreSyntheticClick, markTouchActivation } = useSyntheticClickGuard();

  // States
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  // Derived values
  const target = useSmartLinkTarget(href, eventId);
  const isFormLink = target.kind === "form";
  const formId = target.kind === "form" ? target.formId : null;
  const inviteToken = target.kind === "form" ? target.inviteToken : null;
  const isHotelBookingLink = target.kind === "hotel-booking";
  const bookingLinkId = target.kind === "hotel-booking" ? target.bookingLinkId : null;
  const { open: openHotelBookingModal, modal: hotelBookingModal } = useHotelBookingCta(eventId, bookingLinkId);
  const linkedStepsCta = useLinkedStepsCta({
    href,
    linkedSteps,
    eventId,
    landingPageId: landingPageId || null,
    primaryColor,
    title: label,
    buttonId,
  });
  // One opener per link kind, shared by every click path: a sequence starts at step 1, anything
  // else opens its single modal as before.
  const openFormModal = useCallback(() => setIsFormModalOpen(true), []);
  const openForm = linkedStepsCta.isSequence ? linkedStepsCta.start : openFormModal;
  const openHotelBooking = linkedStepsCta.isSequence ? linkedStepsCta.start : openHotelBookingModal;
  const interactiveClassName = className
    ? `touch-manipulation ${className}`
    : "inline-flex touch-manipulation items-center rounded-xl px-6 py-3 font-medium text-white transition-colors [@media(hover:hover)]:hover:opacity-90";
  // Only buttons that pass a button id can send; the server resolves everything else from the ids.
  const emailSendTarget = useMemo(
    () => (buttonId ? { landingPageId, eventId: pageEventId, buttonId } : null),
    [buttonId, landingPageId, pageEventId]
  );

  // Handlers
  const captureActivation = useCallback(
    (outcome: CtaActivationOutcome) => {
      posthog?.capture(CTA_ACTIVATED_EVENT, {
        landing_page_id: landingPageId,
        event_id: eventId,
        label,
        href,
        outcome,
      });
    },
    [posthog, landingPageId, eventId, label, href]
  );

  // Scrolls to the anchor target and returns whether it exists. A missing target
  // returns false so callers fall back to native navigation instead of a silent no-op.
  const activateAnchor = useCallback((): boolean => {
    const id = href.slice(1);
    const el = document.getElementById(id);
    if (!el) return false;

    const top = el.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset(24);
    window.scrollTo({ top, behavior: "smooth" });
    return true;
  }, [href]);

  const activateLink = useCallback(() => {
    if (isAnchorLink(href)) {
      const scrolled = activateAnchor();
      // Touch handlers preventDefault, so there's no native anchor behavior to fall back
      // on — set the hash ourselves so a missing target still moves the URL (loud, not silent).
      if (!scrolled) window.location.hash = href;
      captureActivation(scrolled ? "anchor" : "anchor_unresolved");
      return;
    }

    if (isExternalLink(href)) {
      captureActivation("external");
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }

    captureActivation("internal");
    window.location.assign(href);
  }, [activateAnchor, captureActivation, href]);

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (shouldIgnoreSyntheticClick()) {
        e.preventDefault();
        return;
      }

      if (!isAnchorLink(href)) {
        // External/internal links: the native <a> handles navigation. Record the click.
        captureActivation(isExternalLink(href) ? "external" : "internal");
        return;
      }

      const scrolled = activateAnchor();
      if (scrolled) {
        // Only take over the click when we actually scrolled. When the target is missing
        // we DON'T preventDefault — the browser performs its native `#hash` navigation
        // (URL updates, attempts its own scroll) instead of swallowing the click silently.
        e.preventDefault();
      }
      captureActivation(scrolled ? "anchor" : "anchor_unresolved");
    },
    [activateAnchor, captureActivation, href, shouldIgnoreSyntheticClick]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      markTouchActivation();
      e.preventDefault();

      if (isFormLink && formId) {
        captureActivation("form");
        openForm();
        return;
      }

      if (isHotelBookingLink && bookingLinkId) {
        captureActivation("hotel_booking");
        openHotelBooking();
        return;
      }

      activateLink();
    },
    [
      activateLink,
      bookingLinkId,
      captureActivation,
      formId,
      isFormLink,
      isHotelBookingLink,
      markTouchActivation,
      openForm,
      openHotelBooking,
    ]
  );

  // Effects
  // Warm the form-schema cache so opening the modal is instant.
  useEffect(() => {
    if (!isFormLink || !formId) return;
    prefetchForm(formId, landingPageId || null);
  }, [isFormLink, formId, landingPageId]);

  // Reject dangerous schemes (javascript:/data:/…) before rendering an href or navigating.
  // Stored links are organizer free-text, so guard at the renderer regardless of the editor.
  // Render it visibly disabled (not a live-looking button) and still record the click so a
  // blocked CTA shows up in analytics rather than only as a rageclick in a session recording.
  if (target.kind === "unsafe") {
    return (
      <button
        type="button"
        aria-disabled="true"
        title="This link can't be opened"
        onClick={() => captureActivation("blocked_scheme")}
        className={`${interactiveClassName} cursor-not-allowed opacity-60`}
        style={{ backgroundColor: primaryColor, ...style }}
      >
        {label}
      </button>
    );
  }

  // Email links (`#__email-{id}`) start with `#`, so without this branch they'd render as a live
  // anchor. Buttons that pass a button id (hero, textButton, navbar) send through EmailSendButton,
  // kept as its own component because its toast hook throws without a provider and SmartButton's
  // other callers have none. Without a button id (pricing, card grid, venue) the link stays inert:
  // disabled, no navigation, no hash change, no analytics event.
  if (target.kind === "email") {
    if (emailSendTarget) {
      return (
        <EmailSendButton
          sendTarget={emailSendTarget}
          label={label}
          className={interactiveClassName}
          style={{ backgroundColor: primaryColor, ...style }}
          onOutcome={captureActivation}
        />
      );
    }

    return (
      <button
        type="button"
        disabled
        className={`${interactiveClassName} cursor-not-allowed opacity-60`}
        style={{ backgroundColor: primaryColor, ...style }}
      >
        {label}
      </button>
    );
  }

  // Render — form link
  if (isFormLink && formId) {
    return (
      <>
        <button
          type="button"
          onClick={() => {
            if (shouldIgnoreSyntheticClick()) return;
            captureActivation("form");
            openForm();
          }}
          onTouchEnd={handleTouchEnd}
          className={interactiveClassName}
          style={{ backgroundColor: primaryColor, ...style }}
        >
          {label}
        </button>
        {linkedStepsCta.isSequence ? (
          linkedStepsCta.modals
        ) : (
          <FormModal
            isOpen={isFormModalOpen}
            onClose={() => setIsFormModalOpen(false)}
            formId={formId}
            eventId={eventId}
            primaryColor={primaryColor}
            orderContext={orderContext}
            inviteToken={inviteToken}
          />
        )}
      </>
    );
  }

  // Render — hotel-booking link
  if (isHotelBookingLink && bookingLinkId) {
    return (
      <>
        <button
          type="button"
          onClick={() => {
            if (shouldIgnoreSyntheticClick()) return;
            captureActivation("hotel_booking");
            openHotelBooking();
          }}
          onTouchEnd={handleTouchEnd}
          // Warm the modal chunk on real intent (hover/touch) rather than unconditionally at idle
          // for every page that happens to have this CTA — most visitors never click it.
          onPointerEnter={prefetchHotelBookingModal}
          onTouchStart={prefetchHotelBookingModal}
          className={interactiveClassName}
          style={{ backgroundColor: primaryColor, ...style }}
        >
          {label}
        </button>
        {linkedStepsCta.isSequence ? linkedStepsCta.modals : hotelBookingModal}
      </>
    );
  }

  // Render — anchor link
  if (isAnchorLink(href)) {
    return (
      <a
        href={href}
        onClick={handleLinkClick}
        onTouchEnd={handleTouchEnd}
        className={interactiveClassName}
        style={{ backgroundColor: primaryColor, ...style }}
      >
        {label}
      </a>
    );
  }

  // Render — external or regular link
  return (
    <a
      href={href}
      onClick={handleLinkClick}
      onTouchEnd={handleTouchEnd}
      className={interactiveClassName}
      style={{ backgroundColor: primaryColor, ...style }}
      target={isExternalLink(href) ? "_blank" : undefined}
      rel={isExternalLink(href) ? "noopener noreferrer" : undefined}
    >
      {label}
    </a>
  );
}
